import React, { useMemo, useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useGetParametreByKeyQuery } from '../../services/coreApi';
import { useGetMessagesEtudiantsQuery } from '../../services/messagesEtudiantsApi';
import { useGetDemandesActivationQuery } from '../../services/demandesActivationApi';
import { useGetMessagesParentsQuery } from '../../services/messagesParentsApi';
import { useGetInscriptionsQuery } from '../../services/inscriptionsParentsApi';
import { useListerSuggestionsQuery } from '../../services/suggestionsApi';
import { useListerConversationsQuery } from '../../services/messagerieChat';
import apiClient from '../../config/api.config';
import {
  LayoutDashboard,
  Users,
  BookOpen,
  Calculator,
  FileText,
  BarChart3,
  Settings,
  PenTool,
  Brain,
  Layers,
  Calendar,
  Target,
  Printer,
  UserCheck,
  List,
  Building2,
  FileDown,
  Search,
  Zap,
  ScrollText,
  ClipboardCheck,
  Shuffle,
  ClipboardList,
  QrCode,
  Wallet,
  Shield,
  Package,
  GraduationCap,
  Sparkles,
  Library,
  FlaskConical,
  Award,
  Inbox,
  Lock,
  TrendingUp,
  MessagesSquare,
  Wifi,
  ChevronDown,
  ChevronRight,
  Home,
  Bookmark,
  Clipboard,
  Archive,
  Radio,
  ScanLine,
  ShieldAlert,
  DoorOpen,
  LayoutGrid,
} from 'lucide-react';

interface NavItem {
  key: string;
  path: string;
  label: string;
  icon: React.ReactNode;
}

interface NavCategory {
  key: string;
  label: string;
  icon: React.ReactNode;
  items: NavItem[];
}

// Détection automatique du campus depuis le hostname.
// - resultat2ibz.com → Brazzaville
// - gestion-notes-2i.com (ou autre) → Pointe-Noire (défaut)
// Une valeur override peut être stockée en localStorage.campus_label pour les
// environnements de dev / tests.
function campusLabel(): string {
  try {
    const override = localStorage.getItem('campus_label');
    if (override) return override;
  } catch { /* */ }
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  if (/resultat2ibz|brazzaville/i.test(host)) return 'Campus de Brazzaville';
  return 'Campus de Pointe-Noire';
}

// ─── Regroupement des onglets par catégorie ──────────────────────────────────
const navCategories: NavCategory[] = [
  {
    key: 'accueil',
    label: 'Accueil',
    icon: <Home size={16} />,
    items: [
      { key: 'dashboard', path: '/dashboard', label: 'Tableau de Bord', icon: <LayoutDashboard size={18} /> },
    ],
  },
  {
    key: 'etudiants',
    label: 'Étudiants',
    icon: <Users size={16} />,
    items: [
      { key: 'etudiants', path: '/etudiants', label: 'Étudiants', icon: <Users size={18} /> },
      { key: 'liste_inscrits', path: '/liste-inscrits', label: 'Liste Inscrits', icon: <ClipboardList size={18} /> },
      { key: 'caisse_resultats', path: '/caisse/resultats', label: 'Résultats étudiants', icon: <GraduationCap size={18} /> },
      { key: 'caisse_acces', path: '/caisse/acces', label: 'Accès étudiants', icon: <Shield size={18} /> },
      { key: 'bascule_specialisation', path: '/bascule-specialisation', label: 'Bascule L3DI&GL', icon: <Users size={18} /> },
      { key: 'options_classes', path: '/gestion-options', label: 'Options & Classes', icon: <Building2 size={18} /> },
    ],
  },
  {
    key: 'pilotage',
    label: 'Pilotage & Service',
    icon: <ClipboardList size={16} />,
    items: [
      { key: 'service_scolarite', path: '/service-scolarite', label: 'Service Scolarité', icon: <ClipboardList size={18} /> },
    ],
  },
  {
    key: 'programme',
    label: 'Programme & UE',
    icon: <BookOpen size={16} />,
    items: [
      { key: 'matieres', path: '/matieres', label: 'Gestion Matières', icon: <Layers size={18} /> },
      { key: 'repartition_ue', path: '/repartition-ue', label: 'Répartition UE', icon: <Sparkles size={18} /> },
      { key: 'audit_notes_ocr', path: '/audit-notes-ocr', label: 'Audit Notes OCR', icon: <Sparkles size={18} /> },
      { key: 'audit_credits', path: '/audit-credits', label: 'Audit Crédits (30)', icon: <Calculator size={18} /> },
      { key: 'synthese_ue', path: '/synthese-repartition-ue', label: 'Synthèse Répartition UE', icon: <ClipboardList size={18} /> },
      { key: 'gestion_tp', path: '/gestion-tp', label: 'Gestion des TP', icon: <FlaskConical size={18} /> },
      { key: 'formateurs', path: '/formateurs', label: 'Formateurs', icon: <UserCheck size={18} /> },
      { key: 'comptes_formateurs', path: '/comptes-formateurs', label: 'Comptes formateurs', icon: <UserCheck size={18} /> },
      { key: 'migration_annee', path: '/migration-annee', label: 'Migration d’année', icon: <Calendar size={18} /> },
    ],
  },
  {
    key: 'copies',
    label: 'Copies & Association',
    icon: <Printer size={16} />,
    items: [
      { key: 'generation_copies', path: '/generation-copies', label: 'Génération Copies', icon: <Printer size={18} /> },
      { key: 'association_copies', path: '/association-copies-barcode', label: 'Association Copies', icon: <UserCheck size={18} /> },
      { key: 'recap_matieres', path: '/recap-matieres-associees', label: 'Recap Matières', icon: <BookOpen size={18} /> },
    ],
  },
  {
    key: 'saisie',
    label: 'Saisie des notes',
    icon: <PenTool size={16} />,
    items: [
      { key: 'saisie_devoirs', path: '/saisie-notes-devoirs', label: 'Notes Devoirs', icon: <PenTool size={18} /> },
      { key: 'saisie_examens', path: '/saisie-notes-examens', label: 'Notes Examens', icon: <BookOpen size={18} /> },
      { key: 'saisie_code_copie', path: '/saisie-par-code-copie', label: 'Saisie par code de copie', icon: <ScanLine size={18} /> },
      { key: 'suivi_anti_reclamations', path: '/suivi-anti-reclamations', label: 'Suivi anti-réclamations', icon: <ShieldAlert size={18} /> },
      { key: 'consultation_notes', path: '/notes', label: 'Consultation Notes', icon: <List size={18} /> },
      { key: 'fenetres_saisie', path: '/fenetres-saisie', label: 'Fenêtres de saisie', icon: <Lock size={18} /> },
    ],
  },
  {
    key: 'ocr',
    label: 'OCR & Scans',
    icon: <QrCode size={16} />,
    items: [
      { key: 'releves_devoirs', path: '/ocr/devoirs', label: 'Générer Relevés Devoirs', icon: <FileDown size={18} /> },
      { key: 'releves_examens', path: '/ocr/generation-correction-v2', label: 'Générer Relevés Examens', icon: <FileDown size={18} /> },
      { key: 'saisie_rapide_devoirs', path: '/ocr/saisie-rapide-devoirs', label: 'Saisie Rapide Devoirs', icon: <Zap size={18} /> },
      { key: 'saisie_rapide_examens', path: '/ocr/saisie-rapide-examens', label: 'Saisie Rapide Examens', icon: <Zap size={18} /> },
      { key: 'upload_qr', path: '/ocr/upload-automatique-qr', label: 'Upload Scan QR', icon: <QrCode size={18} /> },
      { key: 'reclamations', path: '/ocr/reclamations', label: 'Réclamations Notes', icon: <Search size={18} /> },
    ],
  },
  {
    key: 'resultats',
    label: 'Résultats & Délibération',
    icon: <Award size={16} />,
    items: [
      { key: 'resultats', path: '/resultats', label: 'Résultats', icon: <Calculator size={18} /> },
      { key: 'deliberation', path: '/deliberation', label: 'Tableau Délibération', icon: <Award size={18} /> },
      { key: 'pv_deliberation', path: '/pv-deliberation', label: 'PV Délibération', icon: <ClipboardCheck size={18} /> },
      { key: 'suivi_scolarite', path: '/suivi-scolarite', label: 'Suivi Relevés & Sujets', icon: <ClipboardCheck size={18} /> },
      { key: 'rachat', path: '/rachat-intelligent', label: 'Rachat Intelligent', icon: <Brain size={18} /> },
      { key: 'rattrapage_choix', path: '/rattrapage/choix', label: 'Choix Rattrapage', icon: <GraduationCap size={18} /> },
      { key: 'fraudes', path: '/fraudes', label: 'Gestion Fraudes', icon: <Shield size={18} /> },
      { key: 'annulations_session', path: '/annulations-session', label: 'Annulation de session', icon: <Shield size={18} /> },
      { key: 'enveloppes', path: '/enveloppes', label: 'Suivi Enveloppes', icon: <Package size={18} /> },
    ],
  },
  {
    key: 'releves',
    label: 'Relevés PDF',
    icon: <FileText size={16} />,
    items: [
      { key: 'releves_pdf', path: '/releves', label: 'Relevés PDF', icon: <FileText size={18} /> },
      { key: 'releves_notes', path: '/releves/notes', label: 'Relevés de Notes', icon: <ScrollText size={18} /> },
      { key: 'generation_releves', path: '/releves/generation', label: 'Génération Relevés', icon: <Target size={18} /> },
    ],
  },
  {
    key: 'examens',
    label: 'Examens & EDT',
    icon: <Calendar size={16} />,
    items: [
      { key: 'brassage', path: '/brassage', label: 'Brassage Examens', icon: <Shuffle size={18} /> },
      { key: 'emplois_temps', path: '/emplois-temps', label: 'Emplois du Temps', icon: <Calendar size={18} /> },
      { key: 'budget_correction', path: '/budget-correction', label: 'Budget Correction', icon: <Wallet size={18} /> },
      { key: 'banque_sujets', path: '/banque-sujets', label: 'Banque sujets examens', icon: <Library size={18} /> },
      { key: 'depot_sujets', path: '/depot-sujets', label: 'Dépôt sujets (confidentiel)', icon: <Shield size={18} /> },
      { key: 'audit_sujets', path: '/audit-sujets', label: 'Audit téléchargements sujets', icon: <Shield size={18} /> },
      { key: 'audit_agents', path: '/audit-agents', label: 'Audit activité agents', icon: <Shield size={18} /> },
      { key: 'edt_surveillance', path: '/edt-surveillance', label: 'EDT (créneaux)', icon: <Calendar size={18} /> },
      { key: 'salles', path: '/salles', label: 'Salles', icon: <DoorOpen size={18} /> },
      { key: 'occupation_salles', path: '/occupation-salles', label: 'Occupation des salles', icon: <LayoutGrid size={18} /> },
      { key: 'impression_edt', path: '/impression-edt', label: 'Impression EDT', icon: <Printer size={18} /> },
      { key: 'presence_formateurs', path: '/presence-formateurs', label: 'Présence formateurs', icon: <UserCheck size={18} /> },
    ],
  },
  {
    key: 'surveillance',
    label: 'Surveillance & Vie scolaire',
    icon: <ClipboardCheck size={16} />,
    items: [
      { key: 'surveillance', path: '/surveillance', label: 'Surveillance / Appel', icon: <ClipboardCheck size={18} /> },
      { key: 'justifications', path: '/justifications', label: 'Justifications', icon: <ClipboardCheck size={18} /> },
      { key: 'conduite_eleves', path: '/conduite-eleves', label: 'Conduite des élèves', icon: <Award size={18} /> },
      { key: 'dashboard_absences', path: '/dashboard-absences', label: 'Dashboard absences', icon: <BarChart3 size={18} /> },
      { key: 'mon_planning', path: '/mon-planning', label: 'Mon planning', icon: <Calendar size={18} /> },
    ],
  },
  {
    key: 'formateur',
    label: 'Espace formateur',
    icon: <GraduationCap size={16} />,
    items: [
      { key: 'espace_formateur', path: '/formateur/accueil', label: 'Mon Espace (PWA)', icon: <GraduationCap size={18} /> },
      { key: 'saisie_notes_formateur', path: '/saisie-notes-formateur', label: 'Saisir mes notes', icon: <PenTool size={18} /> },
    ],
  },
  {
    key: 'statistiques',
    label: 'Statistiques',
    icon: <BarChart3 size={16} />,
    items: [
      { key: 'statistiques', path: '/statistiques', label: 'Statistiques', icon: <BarChart3 size={18} /> },
      { key: 'participation', path: '/statistiques-participation', label: 'Participation Examens', icon: <Users size={18} /> },
      { key: 'evolution_annuelle', path: '/evolution-annuelle', label: 'Évolution inter-années', icon: <TrendingUp size={18} /> },
    ],
  },
  {
    key: 'communication',
    label: 'Communication',
    icon: <MessagesSquare size={16} />,
    items: [
      { key: 'messagerie_chat', path: '/messagerie', label: 'Chat instantané', icon: <MessagesSquare size={18} /> },
      { key: 'etudiants_en_ligne', path: '/etudiants-en-ligne', label: 'Étudiants en ligne', icon: <Wifi size={18} /> },
      { key: 'portail_admin', path: '/portail-admin', label: 'Portail Étudiant', icon: <GraduationCap size={18} /> },
      { key: 'recouvrement', path: '/recouvrement', label: 'Recouvrement (PWA)', icon: <ScanLine size={18} /> },
      { key: 'actualites', path: '/actualites', label: 'Actualités', icon: <Sparkles size={18} /> },
      { key: 'messages_etudiants', path: '/messages-etudiants', label: 'Messages Étudiants', icon: <Inbox size={18} /> },
      { key: 'inscriptions_parents', path: '/inscriptions-parents', label: 'Inscriptions Parents', icon: <UserCheck size={18} /> },
      { key: 'parents', path: '/parents', label: 'Comptes Parents', icon: <Users size={18} /> },
      { key: 'messages_parents', path: '/messages-parents', label: 'Messages Parents', icon: <Inbox size={18} /> },
      { key: 'moderation_commentaires', path: '/moderation-commentaires', label: 'Modération Commentaires', icon: <Shield size={18} /> },
      { key: 'demandes_activation', path: '/demandes-activation', label: 'Demandes Activation', icon: <Wallet size={18} /> },
      { key: 'suggestions', path: '/suggestions', label: 'Suggestions Étudiants', icon: <Inbox size={18} /> },
    ],
  },
];

// Liste plate (rétro-compat pour composant réglages onglets)
export const ALL_NAV_ITEMS: NavItem[] = navCategories.flatMap(c => c.items);

// ─────────────────────────────────────────────────────────────────────────────
// Visibilité par défaut par rôle.
// ─────────────────────────────────────────────────────────────────────────────
const ROLE_DEFAULT_KEYS: Record<string, string[] | null> = {
  admin: null,
  agent: [
    'dashboard', 'etudiants', 'liste_inscrits', 'bascule_specialisation',
    'service_scolarite',
    'matieres', 'repartition_ue',
    'audit_notes_ocr', 'audit_credits', 'gestion_tp', 'formateurs', 'comptes_formateurs',
    'generation_copies', 'association_copies', 'recap_matieres',
    'saisie_devoirs', 'saisie_examens', 'saisie_code_copie',
    'releves_devoirs', 'releves_examens', 'reclamations',
    'saisie_rapide_devoirs', 'saisie_rapide_examens', 'upload_qr',
    'consultation_notes', 'fraudes', 'enveloppes',
    'rachat', 'rattrapage_choix',
    'resultats', 'deliberation', 'releves_pdf', 'releves_notes',
    'generation_releves', 'pv_deliberation', 'suivi_scolarite', 'brassage',
    'depot_sujets', 'banque_sujets',
    'emplois_temps', 'budget_correction',
    'statistiques', 'participation',
    'options_classes', 'portail_admin', 'actualites',
    'fenetres_saisie',
    'messages_etudiants', 'messagerie_chat', 'etudiants_en_ligne',
    'inscriptions_parents', 'parents', 'messages_parents', 'moderation_commentaires',
    'demandes_activation', 'suggestions',
  ],
  surveillant: [
    'dashboard', 'etudiants',
    'edt_surveillance', 'salles', 'occupation_salles', 'impression_edt', 'presence_formateurs', 'surveillance', 'justifications',
    'conduite_eleves', 'dashboard_absences',
    'messages_parents', 'messagerie_chat', 'etudiants_en_ligne',
  ],
  formateur: [
    'dashboard', 'espace_formateur', 'mon_planning',
    'saisie_notes_formateur', 'conduite_eleves',
    'messages_parents', 'messagerie_chat', 'etudiants_en_ligne',
  ],
  marketing: [
    'dashboard', 'actualites', 'statistiques',
  ],
  bureau_stages: [
    'dashboard', 'etudiants', 'statistiques',
  ],
  // Caisse (2026-10-02) : vérification des résultats avant réinscription.
  // Les autres onglets restent ajoutables via Paramètres → Visibilité onglets.
  agent_caisse: [
    'dashboard', 'etudiants', 'caisse_resultats',
    'demandes_activation', 'caisse_acces',
  ],
  directeur: [
    'dashboard', 'etudiants', 'consultation_notes', 'annulations_session',
    'resultats', 'deliberation', 'releves_pdf',
    'statistiques', 'participation', 'dashboard_absences',
    'messagerie_chat', 'etudiants_en_ligne',
    'edt_surveillance', 'salles', 'occupation_salles', 'impression_edt', 'presence_formateurs',
  ],
  consultation: [
    'dashboard', 'consultation_notes', 'resultats', 'releves_pdf',
    'statistiques',
  ],
  // Compte « invité » (aide pendant les examens) : par défaut, seule
  // l'association des copies + le tableau de bord (toujours visible) sont
  // accessibles. L'admin peut ajouter d'autres onglets par utilisateur via
  // Paramètres → Visibilité des onglets → « Par utilisateur ».
  invite: [
    'dashboard', 'association_copies',
  ],
  // Coordonnateur pédagogique (2026-09-02) : accès limité à la répartition
  // des UEs, la gestion des crédits, la liste des étudiants, la vie scolaire
  // et la surveillance. Le filtre par pôle s'applique automatiquement via
  // `poles_autorises` (get_options_autorisees_via_etudiants côté backend).
  coordonnateur: [
    'dashboard',
    'etudiants',
    'matieres', 'repartition_ue', 'audit_credits',
    'edt_surveillance', 'salles', 'occupation_salles', 'impression_edt', 'presence_formateurs', 'surveillance', 'justifications',
    'conduite_eleves', 'dashboard_absences',
    'options_classes',
    'messagerie_chat', 'etudiants_en_ligne',
  ],
};

// Whitelist appliquée aux comptes de rôle `agent` quand le paramètre
// `mode_restreint_agent_examen` est activé — pendant les sessions d'examens
// on limite drastiquement l'accès pour éviter les erreurs / fuites (le
// dépôt confidentiel des sujets étant le cas d'usage principal).
// Les rubriques listées ici sont les SEULES visibles pour ces agents.
const AGENT_RESTREINT_EXAMEN_KEYS = [
  'dashboard',            // toujours visible (accueil)
  'etudiants',            // gestion des étudiants
  'service_scolarite',    // rédaction rapports quotidiens
  'formateurs',           // gestion formateurs
  'association_copies',   // association copies (page principale examen)
  'emplois_temps',        // EDT
  'participation',        // statistiques participation examens
  'messagerie_chat',      // chat instantané
];

const OPEN_STATE_KEY = 'sidebar_cat_open';

export const Sidebar: React.FC<{ onNavigate?: () => void }> = ({ onNavigate }) => {
  const anneeActive = typeof window !== 'undefined' ? localStorage.getItem('annee_academique') : null;
  const user = useSelector((state: RootState) => state.auth.user);
  const { data: visibiliteParam } = useGetParametreByKeyQuery('onglets_visibilite');
  // Surcouche PAR UTILISATEUR : {hidden: {user_id: [keys]}, shown: {user_id: [keys]}}
  const { data: visibiliteUserParam } = useGetParametreByKeyQuery('onglets_visibilite_user');
  // Mode restreint « session d'examens » — quand ON, les comptes de rôle
  // `agent` ne voient que la whitelist AGENT_RESTREINT_EXAMEN_KEYS.
  const { data: modeRestreintParam } = useGetParametreByKeyQuery('mode_restreint_agent_examen');
  // Liste d'exceptions : IDs des agents qui gardent leur accès complet
  // malgré le mode restreint (ex : ceux qui travaillent à la scolarité).
  const { data: modeRestreintExceptionsParam } = useGetParametreByKeyQuery('mode_restreint_agent_examen_exceptions');

  // Badges divers
  const { data: msgEtuStats } = useGetMessagesEtudiantsQuery({ statut: 'non_traites', limit: 1 }, { pollingInterval: 60_000 });
  const nbMsgEtuNonTraites = msgEtuStats?.nb_non_traites || 0;
  const { data: msgParentStats } = useGetMessagesParentsQuery({ statut: 'non_lus' }, { pollingInterval: 60_000 });
  const nbMsgParentsNonLus = msgParentStats?.nb_total_non_lus || 0;
  const { data: inscriptionsStats } = useGetInscriptionsQuery({ statut: 'en_attente' }, { pollingInterval: 60_000 });
  const nbInscriptionsEnAttente = inscriptionsStats?.nb_en_attente || 0;
  const { data: demandesStats } = useGetDemandesActivationQuery({ statut: 'a_traiter', limit: 1 }, { pollingInterval: 60_000 });
  const nbDemandesATraiter = demandesStats?.nb_a_traiter || 0;
  const { data: suggestionsStats } = useListerSuggestionsQuery({ statut: 'nouvelle' }, { pollingInterval: 60_000 });
  const nbSuggestionsNouvelles = suggestionsStats?.kpis?.nouvelles || 0;
  // Poll aligné sur NotificationCenter (20s) — RTK Query mutualise la même
  // query (cache déduplication) donc les 2 composants partagent 1 fetch.
  const { data: convsData } = useListerConversationsQuery(undefined, { pollingInterval: 20_000 });
  const nbChatNonLus = (convsData?.results || []).reduce((s, c) => s + (c.nb_non_lus || 0), 0);

  // Badge « présence formateurs » : nouvelles saisies des coordonnateurs depuis
  // la dernière consultation du DAAC. Léger (apiClient + poll 60s), limité aux
  // rôles de pilotage qui exploitent le tableau de bord.
  const PRESENCE_VU_KEY = 'presence_formateurs_last_seen';
  const [nbPresenceNouveaux, setNbPresenceNouveaux] = useState(0);
  const roleVoitPresence = ['super_admin', 'admin', 'directeur'].includes(user?.role || '');
  useEffect(() => {
    if (!roleVoitPresence) return;
    let actif = true;
    const since = (() => {
      try { return localStorage.getItem(PRESENCE_VU_KEY) || ''; } catch { return ''; }
    })();
    const fetch = () => {
      apiClient.get('/vie-scolaire/presence-formateurs/resume/', { params: since ? { since } : {} })
        .then((r) => { if (actif) setNbPresenceNouveaux(r.data?.nouveaux || 0); })
        .catch(() => {});
    };
    fetch();
    const id = setInterval(fetch, 60_000);
    const onVu = () => { setNbPresenceNouveaux(0); };
    window.addEventListener('presence-vu', onVu);
    return () => { actif = false; clearInterval(id); window.removeEventListener('presence-vu', onVu); };
  }, [roleVoitPresence]);

  // Filtrer les onglets selon le rôle + surcouches (rôle et par utilisateur)
  const visibleCategories = useMemo(() => {
    let allowedKeys: string[] | null = null;
    if (user?.role !== 'super_admin') {
      const role = user?.role || 'consultation';
      const defaults = ROLE_DEFAULT_KEYS[role];
      allowedKeys = defaults === undefined ? null : defaults;
    }

    // Mode restreint « session d'examens » (paramètre admin) : réduit la
    // whitelist aux 7 rubriques essentielles pour les rôles agents (agent +
    // agent_caisse). N'affecte pas les admins ni les autres rôles.
    const modeRestreintRaw: any =
      (modeRestreintParam as any)?.valeur_convertie ?? modeRestreintParam?.valeur;
    const modeRestreintActif = (() => {
      const v = modeRestreintRaw;
      if (typeof v === 'boolean') return v;
      if (typeof v === 'string') return v === 'true' || v === '1';
      if (typeof v === 'number') return v === 1;
      return false;
    })();
    // Exceptions : IDs des utilisateurs qui gardent l'accès complet même
    // en mode restreint (agents qui travaillent directement à la scolarité).
    const exceptionsRaw: any =
      (modeRestreintExceptionsParam as any)?.valeur_convertie ?? modeRestreintExceptionsParam?.valeur;
    const exceptions: Set<number> = (() => {
      try {
        const arr = typeof exceptionsRaw === 'string' ? JSON.parse(exceptionsRaw) : exceptionsRaw;
        if (Array.isArray(arr)) return new Set(arr.map((x) => Number(x)).filter((n) => !Number.isNaN(n)));
      } catch { /* ignore */ }
      return new Set<number>();
    })();
    const estExcepte = user?.id != null && exceptions.has(Number(user.id));
    if (modeRestreintActif && !estExcepte && (user?.role === 'agent' || user?.role === 'agent_caisse')) {
      // La caisse garde ses écrans de vérification des résultats et des accès.
      allowedKeys = user?.role === 'agent_caisse'
        ? [...AGENT_RESTREINT_EXAMEN_KEYS, 'caisse_resultats', 'caisse_acces']
        : AGENT_RESTREINT_EXAMEN_KEYS;
    }

    // Surcouche backend `onglets_visibilite` (par rôle) : masque en plus
    let hiddenForRole: string[] = [];
    const raw: any = (visibiliteParam as any)?.valeur_convertie ?? visibiliteParam?.valeur;
    if (raw) {
      try {
        const config = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (config && typeof config === 'object') {
          const role = user?.role || 'consultation';
          hiddenForRole = config[role] || [];
        }
      } catch { /* ignore */ }
    }

    // Surcouche PAR UTILISATEUR : hidden = masqués en plus / shown = ajoutés
    // au-delà du défaut du rôle. Format : {hidden:{uid:[...]}, shown:{uid:[...]}}
    let hiddenForUser: string[] = [];
    let shownForUser: string[] = [];
    const rawU: any = (visibiliteUserParam as any)?.valeur_convertie ?? visibiliteUserParam?.valeur;
    if (rawU && user?.id) {
      try {
        const configU = typeof rawU === 'string' ? JSON.parse(rawU) : rawU;
        if (configU && typeof configU === 'object') {
          const uid = String(user.id);
          hiddenForUser = (configU.hidden && configU.hidden[uid]) || [];
          shownForUser = (configU.shown && configU.shown[uid]) || [];
        }
      } catch { /* ignore */ }
    }

    const cats: NavCategory[] = [];
    for (const cat of navCategories) {
      const items = cat.items.filter(item => {
        // Ajouté explicitement pour cet utilisateur → toujours visible
        // (sauf si masqué par une autre surcouche user, qui l'emporte).
        if (shownForUser.includes(item.key) && !hiddenForUser.includes(item.key)) return true;
        if (allowedKeys !== null && !allowedKeys.includes(item.key)) return false;
        if (hiddenForRole.includes(item.key)) return false;
        if (hiddenForUser.includes(item.key)) return false;
        return true;
      });
      if (items.length > 0) cats.push({ ...cat, items });
    }
    return cats;
  }, [user, visibiliteParam, visibiliteUserParam, modeRestreintParam, modeRestreintExceptionsParam]);

  // Mode accordéon (2026-07-09) : un seul dropdown ouvert à la fois,
  // tous fermés par défaut. Améliore la lisibilité quand il y a beaucoup
  // de catégories. La sélection est persistée en localStorage pour restaurer
  // le contexte au reload.
  const [openCat, setOpenCat] = useState<string | null>(() => {
    try {
      const raw = localStorage.getItem(OPEN_STATE_KEY);
      if (raw === null) return null;
      // Rétro-compat : si l'ancien format (map) est stocké, on le rejette
      // et on repart de zéro (tout fermé).
      const parsed = JSON.parse(raw);
      return typeof parsed === 'string' ? parsed : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    try { localStorage.setItem(OPEN_STATE_KEY, JSON.stringify(openCat)); } catch {}
  }, [openCat]);

  const toggleCat = (key: string) => setOpenCat(current => (current === key ? null : key));

  const getBadgeCount = (key: string): number => {
    if (key === 'messages_etudiants') return nbMsgEtuNonTraites;
    if (key === 'demandes_activation') return nbDemandesATraiter;
    if (key === 'messages_parents') return nbMsgParentsNonLus;
    if (key === 'inscriptions_parents') return nbInscriptionsEnAttente;
    if (key === 'suggestions') return nbSuggestionsNouvelles;
    if (key === 'messagerie_chat') return nbChatNonLus;
    if (key === 'presence_formateurs') return nbPresenceNouveaux;
    return 0;
  };

  // Badge cumulé sur la catégorie
  const getCatBadge = (cat: NavCategory): number =>
    cat.items.reduce((s, it) => s + getBadgeCount(it.key), 0);

  return (
    <aside className="w-64 bg-gray-900 text-white h-screen flex flex-col overflow-hidden">
      {/* Logo */}
      <div className="p-4 border-b border-gray-800 flex items-center space-x-3">
        <img src="/logo.png" alt="Logo 2i" className="w-12 h-12 rounded-full bg-white p-0.5" />
        <div className="min-w-0">
          <h2 className="text-base font-bold text-blue-400 leading-tight truncate">Institut International 2i</h2>
          <p className="text-[11px] text-amber-300/90 leading-tight mt-0.5 truncate">{campusLabel()}</p>
          <p className="text-[10px] text-gray-500 leading-tight mt-0.5">Gestion Académique</p>
        </div>
      </div>

      {/* Année académique active */}
      {anneeActive && (
        <div className="mx-4 mt-3 mb-1 px-3 py-2 rounded-lg bg-blue-600/20 border border-blue-500/30">
          <div className="flex items-center gap-2">
            <Calendar size={14} className="text-blue-400 shrink-0" />
            <div>
              <p className="text-[10px] text-blue-300/70 uppercase tracking-wider font-medium">Année active</p>
              <p className="text-sm font-bold text-blue-300">{anneeActive}</p>
            </div>
          </div>
        </div>
      )}

      {/* Navigation par catégories */}
      <nav className="flex-1 px-3 py-3 overflow-y-auto">
        {visibleCategories.map((cat) => {
          const open = openCat === cat.key;
          const catBadge = getCatBadge(cat);
          const isSingle = cat.items.length === 1;
          // Une catégorie à 1 seul item : afficher directement l'item sans header
          if (isSingle) {
            const item = cat.items[0];
            const badge = getBadgeCount(item.key);
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => onNavigate?.()}
                className={({ isActive }) =>
                  `flex items-center space-x-3 px-3 py-2 rounded-lg transition text-sm mb-0.5 ${
                    isActive ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }`
                }
              >
                {item.icon}
                <span className="font-medium flex-1">{item.label}</span>
                {badge > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[20px] h-[20px] text-[10px] font-semibold bg-orange-600 text-white rounded-full px-1.5">
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
              </NavLink>
            );
          }
          return (
            <div key={cat.key} className="mb-1">
              {/* Header catégorie */}
              <button
                onClick={() => toggleCat(cat.key)}
                className="w-full flex items-center gap-2 px-2 py-1.5 text-[11px] uppercase tracking-wider font-semibold text-gray-500 hover:text-gray-300 rounded"
              >
                <span className="text-gray-500">{cat.icon}</span>
                <span className="flex-1 text-left">{cat.label}</span>
                {catBadge > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[16px] h-[16px] text-[9px] font-bold bg-orange-600 text-white rounded-full px-1">
                    {catBadge > 99 ? '99+' : catBadge}
                  </span>
                )}
                {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              </button>
              {open && (
                <div className="mt-0.5 space-y-0.5 pl-1">
                  {cat.items.map((item) => {
                    const badge = getBadgeCount(item.key);
                    return (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        onClick={() => onNavigate?.()}
                        className={({ isActive }) =>
                          `flex items-center space-x-2.5 px-3 py-1.5 rounded-lg transition text-[13px] ${
                            isActive
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                          }`
                        }
                      >
                        <span className="text-gray-400">{item.icon}</span>
                        <span className="font-medium flex-1">{item.label}</span>
                        {badge > 0 && (
                          <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-semibold bg-orange-600 text-white rounded-full px-1.5">
                            {badge > 99 ? '99+' : badge}
                          </span>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-gray-800">
        <NavLink
          to="/settings"
          onClick={() => onNavigate?.()}
          className="flex items-center space-x-3 px-3 py-2.5 rounded-lg text-gray-300 hover:bg-gray-800 hover:text-white transition text-sm"
        >
          <Settings size={20} />
          <span className="font-medium">Paramètres</span>
        </NavLink>
      </div>
    </aside>
  );
};
